import { useMemo, useState } from "react";
import { api } from "../api/client";
import type { ProductDto, StaffOrder } from "../api/types";
import { useAppState } from "../state/appState";
import { useToasts } from "../state/toasts";
import { Modal } from "../ui/components";
import { exclusionLabel, money } from "../ui/format";

interface AddedItem {
  productId: string;
  title: string;
  qty: number;
  unitPrice: number;
  modifiers: { groupId: string; optionId: string; title: string; qty: number; price: number; exclusion: boolean }[];
}

/**
 * Согласованная правка оплаченного заказа (п. 16 ТЗ).
 *
 * Сумму можно только уменьшить: доплата в первой версии не поддерживается,
 * поэтому замена возможна на товар такой же или меньшей стоимости. Итог
 * пересчитывается на месте, чтобы сотрудник назвал клиенту сумму возврата
 * ещё во время звонка, а не после.
 */
export function CorrectionModal({ order, onClose }: { order: StaffOrder; onClose: () => void }) {
  const { snapshot } = useAppState();
  const { run } = useToasts();

  const [quantities, setQuantities] = useState<number[]>(() => order.lines.map(line => line.qty));
  const [added, setAdded] = useState<AddedItem[]>([]);
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [picking, setPicking] = useState(false);
  const [busy, setBusy] = useState(false);

  const outstanding = order.paidAmount - order.refundAmount;

  const preview = useMemo(() => {
    const keptSubtotal = order.lines.reduce((sum, line, index) => sum + line.unitPrice * (quantities[index] ?? 0), 0);
    const addedSubtotal = added.reduce((sum, item) => sum + item.unitPrice * item.qty, 0);
    const subtotal = keptSubtotal + addedSubtotal;

    // Та же формула, что на сервере: доля скидки сохраняется (п. 17 ТЗ).
    const discount = order.subtotal > 0 ? Math.min(subtotal, Math.round((subtotal * order.discount) / order.subtotal)) : 0;
    const total = subtotal - discount;

    return { subtotal, discount, total, refund: outstanding - total, empty: subtotal === 0 };
  }, [order, quantities, added, outstanding]);

  const tooExpensive = preview.total > outstanding;
  const canSubmit = Boolean(reason.trim()) && !preview.empty && !tooExpensive && !busy;

  async function submit() {
    setBusy(true);
    const ok = await run(
      () =>
        api.once.post(`/api/orders/${order.id}/correct`, {
          reason: reason.trim(),
          note: note.trim() || undefined,
          keep: quantities.map((qty, index) => ({ index, qty })),
          add: added.map(item => ({
            productId: item.productId,
            qty: item.qty,
            modifiers: item.modifiers.map(modifier => ({
              groupId: modifier.groupId,
              optionId: modifier.optionId,
              qty: modifier.qty
            }))
          }))
        }),
      preview.refund > 0 ? `Состав изменён, возврат ${money(preview.refund)}` : "Состав изменён"
    );
    setBusy(false);
    if (ok) onClose();
  }

  return (
    <Modal
      title={`Изменение заказа №${order.number}`}
      onClose={onClose}
      actions={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Отмена
          </button>
          <button type="button" className="btn btn-primary" onClick={() => void submit()} disabled={!canSubmit}>
            Сохранить и вернуть {money(Math.max(0, preview.refund))}
          </button>
        </>
      }
    >
      <p className="tiny muted">
        Согласуйте изменения с клиентом по телефону. Увеличить сумму нельзя — замена возможна на товар такой же или
        меньшей стоимости.
      </p>

      <div className="correct-lines">
        {order.lines.map((line, index) => (
          <div className="correct-line" key={`${line.productId}-${index}`}>
            <div>
              <strong>{line.title}</strong>
              <div className="mods">
                {line.modifiers.map((modifier, modifierIndex) => (
                  <span className={modifier.exclusion ? "mod-out" : "mod-add"} key={modifierIndex}>
                    {modifier.exclusion ? exclusionLabel(modifier.title) : `+ ${modifier.title}`}
                  </span>
                ))}
              </div>
              <small className="muted num">{money(line.unitPrice)} за штуку</small>
            </div>

            <div className="stepper">
              <button
                type="button"
                className="btn btn-sm"
                onClick={() =>
                  setQuantities(current => current.map((qty, i) => (i === index ? Math.max(0, qty - 1) : qty)))
                }
              >
                −
              </button>
              <b className="num">{quantities[index]}</b>
              <button
                type="button"
                className="btn btn-sm"
                disabled={(quantities[index] ?? 0) >= line.qty}
                title={(quantities[index] ?? 0) >= line.qty ? "Увеличить количество нельзя" : undefined}
                onClick={() =>
                  setQuantities(current => current.map((qty, i) => (i === index ? Math.min(line.qty, qty + 1) : qty)))
                }
              >
                +
              </button>
              <small className="muted">из {line.qty}</small>
            </div>
          </div>
        ))}
      </div>

      {added.map((item, index) => (
        <div className="correct-line added" key={`added-${index}`}>
          <div>
            <strong>{item.title}</strong>
            <div className="mods">
              {item.modifiers.map((modifier, modifierIndex) => (
                <span className={modifier.exclusion ? "mod-out" : "mod-add"} key={modifierIndex}>
                  {modifier.exclusion ? exclusionLabel(modifier.title) : `+ ${modifier.title}`}
                </span>
              ))}
            </div>
            <small className="muted num">
              {money(item.unitPrice)} × {item.qty}
            </small>
          </div>
          <button
            type="button"
            className="btn btn-sm btn-danger"
            onClick={() => setAdded(current => current.filter((_, i) => i !== index))}
          >
            Убрать
          </button>
        </div>
      ))}

      {picking ? (
        <ReplacementPicker
          products={snapshot?.products ?? []}
          onCancel={() => setPicking(false)}
          onAdd={item => {
            setAdded(current => [...current, item]);
            setPicking(false);
          }}
        />
      ) : (
        <button type="button" className="btn btn-sm" onClick={() => setPicking(true)}>
          + Позиция на замену
        </button>
      )}

      <div className="correct-total">
        <div>
          <span className="muted tiny">Оплачено</span>
          <b className="num">{money(outstanding)}</b>
        </div>
        <div>
          <span className="muted tiny">Новая сумма</span>
          <b className="num">{money(preview.total)}</b>
        </div>
        <div>
          <span className="muted tiny">К возврату</span>
          <b className="num">{money(Math.max(0, preview.refund))}</b>
        </div>
      </div>

      {tooExpensive ? (
        <p className="form-error">Новый состав дороже оплаченного на {money(preview.total - outstanding)}.</p>
      ) : null}
      {preview.empty ? <p className="form-error">Пустой состав — используйте полную отмену заказа.</p> : null}

      <label className="field">
        <span>Причина изменения</span>
        <input
          autoFocus
          value={reason}
          onChange={event => setReason(event.target.value)}
          placeholder="Закончилась котлета"
        />
      </label>

      <label className="field">
        <span>Заметка</span>
        <input value={note} onChange={event => setNote(event.target.value)} placeholder="Клиент согласовал замену" />
      </label>
    </Modal>
  );
}

/** Выбор позиции на замену вместе с обязательными вариантами. */
function ReplacementPicker({
  products,
  onAdd,
  onCancel
}: {
  products: ProductDto[];
  onAdd: (item: AddedItem) => void;
  onCancel: () => void;
}) {
  const available = products.filter(product => !product.archived);
  const [productId, setProductId] = useState(available[0]?.id ?? "");
  const [qty, setQty] = useState(1);
  const [chosen, setChosen] = useState<Record<string, string[]>>({});

  const product = available.find(item => item.id === productId);
  const groups = product?.modifiers ?? [];

  const missing = groups.filter(group => group.required && (chosen[group.id]?.length ?? 0) < group.min);

  function toggle(groupId: string, optionId: string, single: boolean) {
    setChosen(current => {
      const selected = current[groupId] ?? [];
      if (single) return { ...current, [groupId]: [optionId] };
      return {
        ...current,
        [groupId]: selected.includes(optionId)
          ? selected.filter(id => id !== optionId)
          : [...selected, optionId]
      };
    });
  }

  function add() {
    if (!product || missing.length > 0) return;

    const modifiers = groups.flatMap(group =>
      (chosen[group.id] ?? []).map(optionId => {
        const option = group.options.find(candidate => candidate.id === optionId)!;
        return {
          groupId: group.id,
          optionId: option.id,
          title: option.title,
          qty: 1,
          price: option.price,
          exclusion: option.exclusion
        };
      })
    );

    onAdd({
      productId: product.id,
      title: product.title,
      qty,
      unitPrice: product.price + modifiers.reduce((sum, modifier) => sum + modifier.price, 0),
      modifiers
    });
  }

  return (
    <div className="picker">
      <div className="field-row">
        <label className="field">
          <span>Товар</span>
          <select
            value={productId}
            onChange={event => {
              setProductId(event.target.value);
              setChosen({});
            }}
          >
            {available.map(item => (
              <option key={item.id} value={item.id}>
                {item.title} — {money(item.price)}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>Количество</span>
          <input
            inputMode="numeric"
            value={qty}
            onChange={event => setQty(Math.max(1, Number(event.target.value) || 1))}
          />
        </label>
      </div>

      {groups.map(group => (
        <div className="field" key={group.id}>
          <span>
            {group.title}
            {group.required ? " · обязательно" : ""}
          </span>
          <div className="opt-list">
            {group.options.map(option => {
              const single = group.max === 1;
              const selected = (chosen[group.id] ?? []).includes(option.id);
              return (
                <label className={`opt ${selected ? "picked" : ""}`} key={option.id}>
                  <input
                    type={single ? "radio" : "checkbox"}
                    name={`group-${group.id}`}
                    checked={selected}
                    onChange={() => toggle(group.id, option.id, single)}
                  />
                  {option.exclusion ? exclusionLabel(option.title) : option.title}
                  {option.price ? ` +${money(option.price)}` : ""}
                </label>
              );
            })}
          </div>
        </div>
      ))}

      <div className="row-actions">
        <button type="button" className="btn btn-sm btn-primary" onClick={add} disabled={missing.length > 0}>
          Добавить
        </button>
        <button type="button" className="btn btn-sm" onClick={onCancel}>
          Отмена
        </button>
        {missing.length > 0 ? <small className="muted">Выберите: {missing.map(g => g.title).join(", ")}</small> : null}
      </div>
    </div>
  );
}
